import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useSEO } from "@/lib/seo";

/**
 * Plain-English privacy policy. Designed to match COPPA expectations:
 * no marketing trackers, no third-party data sale, parents control everything.
 * Linked from the landing page footer.
 */
export default function Privacy() {
  useSEO({
    title: "Privacy policy",
    description: "Plain-English privacy policy for SonSpeak — what we collect, what we never share, and how parents stay in control.",
    path: "/privacy",
  });
  return (
    <div className="min-h-screen px-4 sm:px-8 py-8" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto">
        <header className="flex items-center gap-3 mb-6">
          <Link to="/" className="pill-btn" data-testid="privacy-back">
            <ArrowLeft size={16} /> Back
          </Link>
          <h1 className="display text-2xl sm:text-3xl font-extrabold flex items-center gap-2">
            <ShieldCheck size={26} className="text-[color:var(--coral)]" />
            Privacy, in plain English
          </h1>
        </header>

        <div className="brutal-card bg-[#FFE9C7] border-[3px] border-[color:var(--ink)] p-4 mb-4 text-sm leading-relaxed" data-testid="privacy-draft-banner">
          <strong>⚠️ Draft — pending counsel review.</strong> This document describes our practices in plain English so families can make informed choices. It is not yet the legal Privacy Policy that will appear after our attorney finishes review. By continuing to use SonSpeak you agree to the practices described below, which are subject to change as we finalise the formal policy. Email <a className="underline font-semibold" href="mailto:hello@sonspeak.com">hello@sonspeak.com</a> with any privacy question and we'll respond personally within two business days.
        </div>

        <div className="brutal-card bg-white p-6 prose-like">
          <p className="text-[color:var(--ink-soft)] mb-5">
            Last updated May 2026. SonSpeak is built for kids, used by parents, and trusted by therapists. Here's exactly what we do and don't do with your data.
          </p>

          <h2 className="display text-xl font-bold mt-4 mb-2">What we store</h2>
          <ul className="list-disc pl-5 space-y-1.5 mb-5">
            <li><strong>Account:</strong> your email and a hashed password (we never see your password).</li>
            <li><strong>Boards:</strong> the tiles, photos, and voice recordings you save. These belong to you.</li>
            <li><strong>Usage:</strong> how often each tile is tapped, so we can show you a 30-day activity chart.</li>
            <li><strong>Privacy-respecting analytics:</strong> we record a small, fixed set of server-side events (e.g. "someone visited the landing page," "someone created an account") so we can understand how the product is being used. IP addresses are one-way hashed before they touch the database. No third-party scripts. No advertising IDs. No cross-site tracking.</li>
          </ul>

          <h2 className="display text-xl font-bold mt-4 mb-2">What we don't do</h2>
          <ul className="list-disc pl-5 space-y-1.5 mb-5">
            <li>We do <strong>not</strong> sell, rent, or share your data with advertisers — ever.</li>
            <li>We do <strong>not</strong> mine your child's voice recordings to train AI models.</li>
            <li>We do <strong>not</strong> require your child's name or photo to use the app.</li>
            <li>We do <strong>not</strong> show ads anywhere in the app.</li>
          </ul>

          <h2 className="display text-xl font-bold mt-4 mb-2">Your child's photos and voice</h2>
          <p className="mb-5">
            Photos and voice recordings are stored only on your own device, in your browser's local storage. They are never uploaded to SonSpeak's servers — our server code strips them from every request and cannot store them.
          </p>

          <h2 className="display text-xl font-bold mt-4 mb-2">Backups and exports</h2>
          <p className="mb-5">
            <strong>Settings → Backup &amp; restore</strong> exports your entire board (every tile, photo, recording) as a single JSON file you can save anywhere. You own this file. It can be re-imported on any device — including yours, if we ever shut down.
          </p>

          <h2 className="display text-xl font-bold mt-4 mb-2">Data deletion</h2>
          <p className="mb-5">
            One tap in <strong>Settings → Danger zone</strong> permanently deletes your account, every board, every photo, and every recording. We don't keep "soft-deleted" copies. Once it's gone, it's gone.
          </p>

          <h2 className="display text-xl font-bold mt-4 mb-2">AI suggestions</h2>
          <p className="mb-5">
            When AI suggestions are turned on, the words currently in your sentence strip are sent to Google's Gemini API to generate the next-word predictions. Tile labels (e.g. "I", "want") are sent — voice recordings and photos are <strong>not</strong>. You can turn AI off in <strong>Settings → AI suggestions</strong> at any time.
          </p>

          <h2 className="display text-xl font-bold mt-4 mb-2">Kids under 13 (COPPA)</h2>
          <p className="mb-5">
            SonSpeak is designed to be set up by a parent, therapist, or caregiver — not the child. The account belongs to the adult. We don't request, collect, or expose anything that would identify a child to anyone outside the household. You can use the app without ever entering your child's real name.
          </p>

          <h2 className="display text-xl font-bold mt-4 mb-2">Sub-processors (the third parties we use)</h2>
          <p className="mb-2 text-sm text-[color:var(--ink-soft)]">
            We deliver SonSpeak with help from a small list of named vendors. Each one only sees the minimum it needs to do its job. None of them are advertisers. None of them train AI models on your child's data.
          </p>
          <ul className="list-disc pl-5 space-y-1.5 mb-3 text-sm">
            <li><strong>AI provider</strong> (for AI suggestions &amp; symbol generation, opt-in): receives only the short text labels currently in your sentence — never voice, never photos, never your child's name. We use API tiers whose published terms exclude using requests to train consumer models. AI suggestions can be turned off at any time.</li>
            <li><strong>Resend</strong> (transactional email): receives the recipient address and the email body for invites, password resets, and account emails. No marketing email is sent.</li>
            <li><strong>Stripe</strong> (payments — only when you choose to pay): handles card details directly; SonSpeak never sees them. Children never interact with Stripe.</li>
            <li><strong>MongoDB</strong> (our database): stores your boards, photos, and recordings until you delete your account.</li>
            <li><strong>AbuseIPDB</strong> (security only): receives anonymous server IPs of suspicious traffic to block bots. Never receives user data or content.</li>
            <li><strong>Google Workspace</strong> (inbound email only — for replies to <span className="whitespace-nowrap">hello@/support@/press@</span> etc.): receives whatever a parent or therapist types into a reply. Paid Workspace plans contractually exclude consumer-style training on customer data.</li>
          </ul>
          <p className="text-sm text-[color:var(--ink-soft)] mb-5">
            We deliberately do <strong>not</strong> use Google Analytics, ad pixels, social-login trackers, session-replay tools, or any cross-site tracker. We are happy to share our full sub-processor audit with parents, therapists, schools, or counsel on request — just email us.
          </p>

          <h2 className="display text-xl font-bold mt-4 mb-2">Questions or requests</h2>
          <p>
            Email us at <a className="font-semibold underline" href="mailto:hello@sonspeak.com">hello@sonspeak.com</a> and we'll get back within two business days. You can request your data, delete your data, or just ask us anything — we read every email.
          </p>
        </div>

        <p className="text-center text-xs text-[color:var(--ink-soft)] mt-6">
          SonSpeak · Built by parents, for parents · No ads · No tracking
        </p>
      </div>
    </div>
  );
}
